// Direct backend authentication - no NextAuth.js
import { getApiUrl } from '@/utils/api-base-url';
import { extractValidationMessageKey } from '@/lib/api-errors';
import { logger } from './logger';

interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  scope?: string | string[];
  error?: string;
  errorDescription?: string;
  expiresAt?: number; // Actual expiration timestamp (milliseconds since epoch)
}

class UserProfile {
  constructor(
    public id: string,
    public firstName: string | undefined,
    public lastName: string | undefined,
    public email: string,
    public roles: string[],
    public scopes: string[],
    public lastLoginAt?: Date
  ) {}

  // Computed property for display name - always show email if no name available
  get name(): string {
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }
    if (this.firstName) {
      return this.firstName;
    }
    if (this.lastName) {
      return this.lastName;
    }
    // Always show email if no name is available - never show "Unknown User"
    return this.email;
  }

  // Get display name with proper fallback logic
  getDisplayName(): string {
    return this.name; // This now properly handles the email fallback
  }

  // Static method to create UserProfile from plain object (for localStorage deserialization)
  static fromPlainObject(obj: Record<string, unknown>): UserProfile {
    return new UserProfile(
      obj.id as string,
      obj.firstName as string,
      obj.lastName as string,
      obj.email as string,
      (obj.roles || []) as string[],
      (obj.scopes || []) as string[],
      obj.lastLoginAt ? new Date(obj.lastLoginAt as string) : undefined
    );
  }

  // Check if user has a name (firstName or lastName)
  hasName(): boolean {
    return !!(this.firstName || this.lastName);
  }
}

interface AuthState {
  user: UserProfile | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Window event dispatched when the session is expired/invalid and could not
 * be refreshed. The auth context listens for it to clear React auth state and
 * redirect to the sign-in page.
 */
export const SESSION_EXPIRED_EVENT = 'cvmatcher:session-expired';

class DirectAuthService {
  private baseUrl: string;
  private clientId: string;
  private scope: string;
  private _loggingOut = false;
  /**
   * The in-flight single-use refresh, shared by every concurrent caller
   * (the proactive `ensureValidToken` path and the reactive
   * `refreshSessionAfterUnauthorized` path alike). Backend refresh tokens are
   * single-use, so only one `/connect/token` POST may win; memoizing the
   * promise makes parallel callers await the same result instead of racing to
   * spend the token and logging each other out.
   */
  private refreshPromise: Promise<AuthTokens | null> | null = null;

  constructor() {
    this.baseUrl = getApiUrl();
    this.clientId = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID || 'cvmatcher_web';
    this.scope =
      process.env.NEXT_PUBLIC_AUTH_SCOPE || 'cvmatcher_api offline_access';
  }

  get isLoggingOut(): boolean {
    return this._loggingOut;
  }

  async login(email: string, password: string): Promise<AuthState> {
    this._loggingOut = false;
    const formBody = this.createTokenForm(email);
    formBody.append('grant_type', 'password');
    formBody.append('password', password);

    return this.authenticateWithTokenEndpoint(formBody, 'login');
  }

  async loginWithCode(email: string, code: string): Promise<AuthState> {
    this._loggingOut = false;
    const formBody = this.createTokenForm(email);
    formBody.append('grant_type', 'email_code');
    formBody.append('code', code);

    return this.authenticateWithTokenEndpoint(formBody, 'email code login');
  }

  async requestLoginCode(email: string): Promise<void> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/auth/request-login-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      const parsedBody = await this.parseResponseBody(
        response,
        'login code request'
      );

      if (!response.ok) {
        throw new Error(
          this.extractErrorMessage(parsedBody, 'auth.signIn.codeRequestFailed')
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(this.getNetworkAwareErrorMessage(error));
      }

      throw new Error('auth.signIn.codeRequestFailed');
    }
  }

  private createTokenForm(email: string): URLSearchParams {
    const formBody = new URLSearchParams();
    formBody.append('client_id', this.clientId);
    formBody.append('scope', this.scope);
    formBody.append('username', email);
    return formBody;
  }

  private async authenticateWithTokenEndpoint(
    formBody: URLSearchParams,
    logContext: string
  ): Promise<AuthState> {
    try {
      const response = await fetch(`${this.baseUrl}/connect/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      const parsedBody = await this.parseResponseBody(response, logContext);

      if (!response.ok) {
        return {
          user: null,
          tokens: null,
          isLoading: false,
          error: this.extractErrorMessage(
            parsedBody,
            'validation.auth.credentials.invalid'
          ),
        };
      }

      const tokens = this.normalizeTokens(parsedBody);
      const userProfile = await this.resolveUserProfile(tokens);

      return {
        user: userProfile,
        tokens: tokens,
        isLoading: false,
        error: null,
      };
    } catch (error) {
      // Determine if this is a network error
      let errorMessage = 'Unknown error occurred';

      if (error instanceof Error) {
        // Check for common network error indicators
        const isNetworkError =
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError') ||
          error.message.includes('ECONNREFUSED') ||
          error.message.includes('ENOTFOUND') ||
          error.message.includes('ECONNRESET') ||
          error.message.includes('ERR_NETWORK') ||
          error.message.includes('timeout') ||
          error.message.includes('Unable to connect') ||
          (error.name === 'TypeError' && error.message.includes('fetch'));

        if (isNetworkError) {
          errorMessage = 'validation.auth.network.error';
        } else {
          errorMessage = error.message;
        }
      }

      return {
        user: null,
        tokens: null,
        isLoading: false,
        error: this.getNetworkAwareErrorMessage(errorMessage),
      };
    }
  }

  private async parseResponseBody(
    response: Response,
    logContext: string
  ): Promise<unknown> {
    const responseBody = await response.text();

    if (!responseBody) {
      return null;
    }

    try {
      return JSON.parse(responseBody);
    } catch (error) {
      logger.warn(
        `[AuthDirect] Failed to parse ${logContext} response body`,
        error
      );
      return null;
    }
  }

  private extractErrorMessage(parsedBody: unknown, fallback: string): string {
    const validationKey = extractValidationMessageKey(parsedBody);
    if (validationKey) {
      return validationKey;
    }

    if (typeof parsedBody === 'object' && parsedBody !== null) {
      const body = parsedBody as Record<string, unknown>;
      const errorDescription = body.errorDescription ?? body.error_description;
      if (typeof errorDescription === 'string' && errorDescription) {
        return errorDescription;
      }

      // RFC 7807 ProblemDetails (the backend's canonical error envelope)
      if (typeof body.detail === 'string' && body.detail) {
        return body.detail;
      }

      if (typeof body.message === 'string' && body.message) {
        return body.message;
      }

      if (typeof body.error === 'string' && body.error) {
        return body.error;
      }
    }

    return fallback;
  }

  private getNetworkAwareErrorMessage(error: Error | string): string {
    const message = typeof error === 'string' ? error : error.message;
    const name = typeof error === 'string' ? '' : error.name;

    const isNetworkError =
      message.includes('Failed to fetch') ||
      message.includes('NetworkError') ||
      message.includes('ECONNREFUSED') ||
      message.includes('ENOTFOUND') ||
      message.includes('ECONNRESET') ||
      message.includes('ERR_NETWORK') ||
      message.includes('timeout') ||
      message.includes('Unable to connect') ||
      (name === 'TypeError' && message.includes('fetch'));

    return isNetworkError ? 'validation.auth.network.error' : message;
  }

  private async resolveUserProfile(tokens: AuthTokens): Promise<UserProfile> {
    const userProfile = await this.fetchCurrentUserProfile(tokens);
    if (userProfile) {
      return userProfile;
    }

    const payload = this.parseJWT(tokens.accessToken);
    const extractString = (value: unknown): string =>
      typeof value === 'string' ? value : '';

    return new UserProfile(
      extractString(
        payload.sub ||
          payload[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
          ]
      ),
      extractString(
        payload.given_name ||
          payload[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'
          ] ||
          payload.firstName
      ),
      extractString(
        payload.family_name ||
          payload[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'
          ] ||
          payload.lastName
      ),
      extractString(
        payload.email ||
          payload[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
          ]
      ),
      this.extractRoles(payload),
      Array.isArray(tokens.scope)
        ? tokens.scope
        : typeof tokens.scope === 'string'
          ? tokens.scope.split(' ')
          : typeof payload.scope === 'string'
            ? payload.scope.split(' ')
            : []
    );
  }

  private extractRoles(payload: Record<string, unknown>): string[] {
    if (Array.isArray(payload.role)) {
      return payload.role.map(role => String(role));
    }

    if (typeof payload.role === 'string') {
      return [payload.role];
    }

    const microsoftRole =
      payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    if (Array.isArray(microsoftRole)) {
      return microsoftRole.map(role => String(role));
    }

    if (typeof microsoftRole === 'string') {
      return [microsoftRole];
    }

    return [];
  }

  async logout(): Promise<void> {
    this._loggingOut = true;

    // Revoke the refresh token server-side first — clearing storage alone leaves
    // a copied token able to mint access tokens until its natural expiry.
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('auth_tokens');
        const refreshToken = stored ? JSON.parse(stored)?.refreshToken : null;
        if (refreshToken) {
          await fetch(`${this.baseUrl}/connect/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ refresh_token: refreshToken }),
            credentials: 'include',
            keepalive: true,
          });
        }
      } catch {
        // Best effort — local logout must succeed even when the API is unreachable.
      }

      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('auth_user');
    }
  }

  /**
   * Force a token refresh after a request came back 401 even though the
   * stored token still looked valid locally (revoked token, clock skew, …).
   * Returns true when new tokens were obtained and stored.
   */
  async refreshSessionAfterUnauthorized(): Promise<boolean> {
    if (this._loggingOut) {
      return false;
    }

    const newTokens = await this.performSingleFlightRefresh();
    return !!newTokens?.accessToken;
  }

  /**
   * Refresh the stored session exactly once per burst of concurrent callers.
   * Reads the current refresh token from storage, POSTs `/connect/token`, and
   * persists the rotated tokens. Parallel invocations share the same in-flight
   * promise (memoized in `refreshPromise` and cleared in `finally`), so a
   * single-use refresh token is spent by at most one request. Returns the new
   * tokens on success or `null` when there is nothing to refresh with or the
   * refresh fails; storage is only written on success, so a failed refresh
   * never clobbers tokens a prior successful refresh already stored.
   */
  private performSingleFlightRefresh(): Promise<AuthTokens | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshRun = (async (): Promise<AuthTokens | null> => {
      const auth = this.getStoredAuth();
      const refreshToken = auth.tokens?.refreshToken;
      if (!auth.user || !refreshToken) {
        return null;
      }

      const newTokens = await this.refreshToken(refreshToken);
      if (!newTokens?.accessToken) {
        return null;
      }

      this.storeAuth(auth.user, newTokens);
      return newTokens;
    })();

    this.refreshPromise = refreshRun;
    void refreshRun.finally(() => {
      if (this.refreshPromise === refreshRun) {
        this.refreshPromise = null;
      }
    });

    return refreshRun;
  }

  /**
   * Clear the stored session and broadcast SESSION_EXPIRED_EVENT so the auth
   * context can reset its React state and redirect to sign-in. Called when a
   * request stays 401 after the single refresh-and-replay attempt.
   */
  notifySessionExpired(): void {
    void this.logout();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens | null> {
    try {
      const formBody = new URLSearchParams();
      formBody.append('client_id', this.clientId);
      formBody.append('grant_type', 'refresh_token');
      formBody.append('refresh_token', refreshToken);

      const response = await fetch(`${this.baseUrl}/connect/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      const responseBody = await response.text();
      let parsedBody: unknown;
      try {
        parsedBody = JSON.parse(responseBody);
      } catch (error) {
        logger.warn(
          '[AuthDirect] Failed to parse refresh response body',
          error
        );
        parsedBody = null;
      }

      if (!response.ok) {
        return null;
      }

      return this.normalizeTokens(parsedBody);
    } catch (error) {
      logger.warn('[AuthDirect] Token refresh failed', error);
      return null;
    }
  }

  getStoredAuth(): AuthState {
    if (typeof window === 'undefined') {
      return { user: null, tokens: null, isLoading: false, error: null };
    }

    try {
      const tokensStr = localStorage.getItem('auth_tokens');
      const userStr = localStorage.getItem('auth_user');

      if (!tokensStr || !userStr) {
        return { user: null, tokens: null, isLoading: false, error: null };
      }

      let tokens: AuthTokens;
      try {
        tokens = this.normalizeTokens(JSON.parse(tokensStr) as AuthTokens);
      } catch (parseError) {
        logger.error(
          '[AuthDirect] Failed to parse tokens JSON:',
          parseError,
          'Raw string:',
          tokensStr
        );
        return {
          user: null,
          tokens: null,
          isLoading: false,
          error: 'Failed to parse stored tokens',
        };
      }

      // Properly reconstruct UserProfile from plain object
      let user: UserProfile;
      try {
        const userObj = JSON.parse(userStr);
        user = UserProfile.fromPlainObject(userObj);
      } catch (parseError) {
        logger.error(
          '[AuthDirect] Failed to parse user JSON:',
          parseError,
          'Raw string:',
          userStr
        );
        return {
          user: null,
          tokens: null,
          isLoading: false,
          error: 'Failed to parse stored user',
        };
      }

      return { user, tokens, isLoading: false, error: null };
    } catch (error) {
      logger.error('[AuthDirect] Failed to load stored auth state', error);
      return {
        user: null,
        tokens: null,
        isLoading: false,
        error: 'Failed to load stored authentication',
      };
    }
  }

  async ensureValidToken(): Promise<AuthTokens | null> {
    if (this._loggingOut) {
      return null;
    }

    const auth = this.getStoredAuth();
    if (!auth.tokens) {
      return null;
    }

    const expirationTime = this.getTokenExpiration(auth.tokens);
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

    // If token is expired or about to expire, try to refresh. All concurrent
    // callers share one in-flight refresh so a single-use refresh token is
    // spent at most once (see performSingleFlightRefresh).
    if (Date.now() >= expirationTime - bufferTime) {
      if (auth.tokens.refreshToken) {
        const newTokens = await this.performSingleFlightRefresh();
        if (newTokens) {
          return newTokens;
        }
        // Refresh failed: clear storage AND notify React auth state so the UI
        // never renders a logged-in shell with a dead session (issue 49).
        this.notifySessionExpired();
        return null;
      } else {
        // No refresh token: same zombie-session risk — clear + notify.
        this.notifySessionExpired();
        return null;
      }
    }

    return auth.tokens;
  }

  storeAuth(user: UserProfile, tokens: AuthTokens): void {
    if (typeof window !== 'undefined') {
      const expiresAt = this.getTokenExpiration(tokens);
      const tokensWithExpiration: AuthTokens = { ...tokens, expiresAt };
      localStorage.setItem('auth_tokens', JSON.stringify(tokensWithExpiration));
      localStorage.setItem('auth_user', JSON.stringify(user));
    }
  }

  private getTokenExpiration(tokens: AuthTokens): number {
    const expirationFromToken =
      typeof tokens.accessToken === 'string'
        ? this.extractExpirationFromToken(tokens.accessToken)
        : null;

    if (expirationFromToken) {
      return expirationFromToken;
    }

    if (
      typeof tokens.expiresAt === 'number' &&
      Number.isFinite(tokens.expiresAt)
    ) {
      return tokens.expiresAt;
    }

    if (
      typeof tokens.expiresIn === 'number' &&
      Number.isFinite(tokens.expiresIn)
    ) {
      return Date.now() + tokens.expiresIn * 1000;
    }

    // Fallback: force a short-lived expiration so refresh attempts are triggered
    return Date.now() + 5 * 60 * 1000;
  }

  isAuthenticated(): boolean {
    const auth = this.getStoredAuth();
    return !!auth.user && !!auth.tokens;
  }

  getCurrentUser(): UserProfile | null {
    const auth = this.getStoredAuth();
    return auth.user;
  }

  private parseJWT(token: string): Record<string, unknown> {
    try {
      if (!token || typeof token !== 'string') {
        logger.error('Invalid token provided to parseJWT');
        return {};
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        logger.error(
          'Invalid JWT format - token must have 3 parts separated by dots'
        );
        return {};
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      logger.error('Error parsing JWT token:', error);
      return {};
    }
  }

  private normalizeTokens(rawTokens: unknown): AuthTokens {
    const tokens = (rawTokens ?? {}) as Record<string, unknown>;
    const accessToken =
      typeof tokens.accessToken === 'string'
        ? tokens.accessToken
        : typeof tokens.access_token === 'string'
          ? tokens.access_token
          : '';

    const refreshToken =
      typeof tokens.refreshToken === 'string'
        ? tokens.refreshToken
        : typeof tokens.refresh_token === 'string'
          ? tokens.refresh_token
          : undefined;

    const tokenType =
      typeof tokens.tokenType === 'string'
        ? tokens.tokenType
        : typeof tokens.token_type === 'string'
          ? tokens.token_type
          : 'Bearer';

    const expiresInRaw =
      typeof tokens.expiresIn === 'number'
        ? tokens.expiresIn
        : typeof tokens.expires_in === 'number'
          ? tokens.expires_in
          : typeof tokens.expiresIn === 'string'
            ? Number(tokens.expiresIn)
            : typeof tokens.expires_in === 'string'
              ? Number(tokens.expires_in)
              : undefined;

    const scopeRaw = tokens.scope as unknown;
    const scope = Array.isArray(scopeRaw)
      ? scopeRaw.map(value => String(value))
      : typeof scopeRaw === 'string'
        ? scopeRaw.split(' ')
        : [];

    const expirationFromToken = this.extractExpirationFromToken(accessToken);
    const expiresAt =
      typeof tokens.expiresAt === 'number' && Number.isFinite(tokens.expiresAt)
        ? tokens.expiresAt
        : (expirationFromToken ??
          (typeof expiresInRaw === 'number' && Number.isFinite(expiresInRaw)
            ? Date.now() + expiresInRaw * 1000
            : undefined));

    return {
      accessToken,
      refreshToken,
      tokenType,
      expiresIn:
        typeof expiresInRaw === 'number' && Number.isFinite(expiresInRaw)
          ? expiresInRaw
          : 0,
      scope,
      expiresAt,
    };
  }

  private extractExpirationFromToken(token: string): number | null {
    if (!token) {
      return null;
    }

    try {
      const payload = this.parseJWT(token);
      if (payload?.exp) {
        const expMs = Number(payload.exp) * 1000;
        return Number.isFinite(expMs) ? expMs : null;
      }
    } catch (error) {
      logger.debug('[AuthDirect] Failed to extract token expiration', error);
    }

    return null;
  }

  async fetchCurrentUserProfile(
    tokens: AuthTokens
  ): Promise<UserProfile | null> {
    if (this._loggingOut) {
      logger.debug('[AuthDirect] Skipping profile fetch — logout in progress');
      return null;
    }

    try {
      if (!tokens.accessToken) {
        logger.error('No access token provided');
        return null;
      }

      const payload = this.parseJWT(tokens.accessToken);
      const userId =
        payload.sub ||
        payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
        ];

      if (!userId) {
        logger.error('User ID not found in token');
        return null;
      }

      const response = await fetch(
        `${this.baseUrl}/api/auth/profile/${userId}`,
        {
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch user profile:', response.status);
        return null;
      }

      const userData = await response.json();

      return new UserProfile(
        userData.id,
        userData.firstName,
        userData.lastName,
        userData.email,
        userData.roles || [],
        userData.scopes || [],
        userData.lastLoginAt ? new Date(userData.lastLoginAt) : undefined
      );
    } catch (error) {
      logger.error('[AuthDirect] Error fetching user profile', error);
      return null;
    }
  }
}

export const authService = new DirectAuthService();
export type { AuthState, AuthTokens, UserProfile };
